/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import React, { FC, Fragment, useEffect, useRef, useState } from "react";
import { Dropdown } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import DownloadIcon from "@src/assets/icons/download.svg";
import DownloadWhiteIcon from "@src/assets/icons/download-white.svg";
import {
  DownloadAsMenuData,
  DownloadAsOption,
  DownloadAsOptionId,
  DownloadAsScope,
  getDownloadAsDescriptionKey,
  getDownloadAsOptions,
  hasImage,
} from "@src/utils/downloadAs";

// Block repeat clicks while the BE builds the file.
const DOWNLOAD_COOLDOWN_MS = 5000;
// Module-level so the lock survives a remount.
let lockedUntil = 0;
// 8px gap to the button.
const POPPER_CONFIG = {
  modifiers: [{ name: "offset", options: { offset: [0, 8] } }],
};

const OPTION_META: Record<
  DownloadAsOptionId,
  { icon: string; titleKey: string; tag: string }
> = {
  jpeg: { icon: "fa-file-image", titleKey: "jpeg_image", tag: ".jpg" },
  dicom: { icon: "fa-file-waveform", titleKey: "dicom_file", tag: ".dcm" },
  video: { icon: "fa-file-video", titleKey: "video_file", tag: ".mp4" },
  zip: { icon: "fa-file-zipper", titleKey: "zip_file", tag: ".zip" },
};

interface IDownloadAsItem {
  option: DownloadAsOption;
  data: DownloadAsMenuData;
  onSelect: (option: DownloadAsOption) => void;
}

const DownloadAsItem: FC<IDownloadAsItem> = ({ option, data, onSelect }) => {
  const { t } = useTranslation();
  const meta = OPTION_META[option.id];
  const handleClick = () => onSelect(option);

  return (
    <Dropdown.Item className="download-as__item" onClick={handleClick}>
      <span className="download-as__tile">
        <i className={`fa ${meta.icon}`} />
      </span>
      <span className="download-as__labels">
        <span className="download-as__title">
          {t(`viewer_modal.${meta.titleKey}`)}
        </span>
        <span className="download-as__desc">
          {t(`viewer_modal.${getDownloadAsDescriptionKey(option, data)}`)}
        </span>
      </span>
      <span className="download-as__tag">{meta.tag}</span>
    </Dropdown.Item>
  );
};

interface IDownloadAsMenu extends DownloadAsMenuData {
  // viewer: dark, opens upward. card: light, opens downward.
  variant: "viewer" | "card";
  showLabel?: boolean;
  showChevron?: boolean;
  disabled?: boolean;
  // Controlled, so the parent can keep one popover open.
  open?: boolean;
  onOpenChange?: (nextOpen: boolean) => void;
  toggleClassName?: string;
}

export const DownloadAsMenu: FC<IDownloadAsMenu> = ({
  variant,
  imageCount,
  frames,
  anyOtherMultiframe,
  onSelect,
  showLabel = false,
  showChevron = true,
  disabled = false,
  open: controlledOpen,
  onOpenChange,
  toggleClassName,
}) => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(() => Date.now() < lockedUntil);
  const [innerOpen, setInnerOpen] = useState(false);
  const open = controlledOpen ?? innerOpen;
  const timer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    const remaining = lockedUntil - Date.now();
    if (remaining > 0) {
      timer.current = setTimeout(() => setBusy(false), remaining);
    }
    return () => clearTimeout(timer.current);
  }, []);

  const data = { imageCount, frames, anyOtherMultiframe, onSelect };
  const groups = getDownloadAsOptions(data);

  const handleSelect = (option: DownloadAsOption) => {
    if (busy) return;
    setBusy(true);
    lockedUntil = Date.now() + DOWNLOAD_COOLDOWN_MS;
    onSelect(option);
    timer.current = setTimeout(() => setBusy(false), DOWNLOAD_COOLDOWN_MS);
  };
  const handleToggle = (nextShow: boolean) => {
    setInnerOpen(nextShow);
    onOpenChange?.(nextShow);
  };
  // Do not open the viewer (card click).
  const stopPropagation = (e: React.MouseEvent) => e.stopPropagation();

  const getGroupLabel = (kind: DownloadAsScope) => {
    if (kind === "series") {
      return t("viewer_modal.group_all_images", { images: imageCount });
    }
    // No frame count for a non-image.
    if (!hasImage(frames)) return t("viewer_modal.group_this_instance");
    return variant === "viewer"
      ? t("viewer_modal.group_this_image", { frames })
      : t("viewer_modal.group_first_image");
  };

  const isViewer = variant === "viewer";

  return (
    <Dropdown
      className={`download-as download-as--${variant}`}
      drop={isViewer ? "up" : "down"}
      align="end"
      show={open}
      onToggle={handleToggle}
      onClick={stopPropagation}
    >
      <Dropdown.Toggle
        variant={isViewer ? "primary" : "link"}
        className={`download-as__toggle ${toggleClassName ?? ""}`}
        disabled={busy || disabled}
        aria-haspopup="menu"
        aria-label={t("viewer_modal.download_as")}
      >
        <img
          className={isViewer ? "padding-b-3px" : undefined}
          src={isViewer ? DownloadWhiteIcon : DownloadIcon}
          alt="Download Icon"
        />
        {showLabel && <span>{t("viewer_modal.download_as")}</span>}
        {showChevron && (
          <i
            className={`fa fa-chevron-${
              open ? "up" : "down"
            } download-as__chevron`}
          />
        )}
      </Dropdown.Toggle>
      <Dropdown.Menu className="download-as__menu" popperConfig={POPPER_CONFIG}>
        {groups.map((group) => (
          <Fragment key={group.kind ?? "all"}>
            {group.kind && (
              <Dropdown.Header className="download-as__group">
                {getGroupLabel(group.kind)}
              </Dropdown.Header>
            )}
            {group.options.map((option) => (
              <DownloadAsItem
                key={`${option.scope}-${option.id}`}
                option={option}
                data={data}
                onSelect={handleSelect}
              />
            ))}
          </Fragment>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
};
